import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ExtensionApiFactory } from '~cdw-ae/common/frontend/browser-extension-api';

import { ExtensionCheckinResponse } from './check-in.interfaces';

import type { RootState } from '../extension-store/extension.store';
import { BaseInitialState, StateStatus } from '../extension-store/interfaces/extension-store.interfaces';
import GopherBuddyApiService from '../gb-api/gopher-buddy-api.service';

interface InitialExtensionCheckinState extends BaseInitialState {
  isCustomerValid: boolean; // A single flag if the user is licensed, authorized, and configured
  extensionCheckinResponse?: ExtensionCheckinResponse;
  lastCheckIn: string;
}

const initialState: InitialExtensionCheckinState = {
  isCustomerValid: false,
  error: null,
  status: StateStatus.IDLE,
  lastCheckIn: new Date(Date.now()).toString(), // Default to now
};

const setLastCheckinTime = async () => {
  const extensionApi = ExtensionApiFactory();
  await extensionApi.setLocalStorage({ checkinTime: String(new Date().getTime()) });
};

const inCheckinWindow = async () => {
  const extensionApi = ExtensionApiFactory();
  const notificationTimes = await extensionApi.getLocalStorage<{ checkinTime: string }>('checkinTime');

  if (!notificationTimes?.checkinTime) {
    return true;
  }
  const currentTime = new Date().getTime();
  return currentTime - Number(notificationTimes.checkinTime) > 1 * 60 * 1000; // 1 minuets
};

export const CheckInEffects = {
  checkIn: createAsyncThunk(
    'CheckIn/checkIn',
    async (arg, thunkState): Promise<ExtensionCheckinResponse | undefined> => {
      const state: RootState = thunkState.getState() as RootState;
      // limit the checkin to once every 1 minutes
      // prevents the user from spamming the reload button or prevents the extension from spamming the server
      if (state?.checkInState?.extensionCheckinResponse && !(await inCheckinWindow())) {
        return undefined;
      }
      await setLastCheckinTime();
      return GopherBuddyApiService.CheckIn.get(
        state.userProfile.userProfile.domain,
        state.deviceDetails.details.id,
        state.userProfile.userProfile.email
      );
    }
  ),
};

export const CheckInState = createSlice({
  name: 'CheckIn',
  initialState,
  reducers: {
    setCustomerValid: (mutableState, action: PayloadAction<boolean>) => {
      Object.assign(mutableState, { isCustomerValid: action.payload });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(CheckInEffects.checkIn.pending, (mutableState) => {
      mutableState.status = StateStatus.LOADING;
    });
    builder.addCase(CheckInEffects.checkIn.fulfilled, (mutableState, action) => {
      if (!action.payload) return;
      mutableState.status = StateStatus.SUCCESS;
      mutableState.extensionCheckinResponse = action.payload;
      mutableState.lastCheckIn = new Date(Date.now()).toString();
      mutableState.isCustomerValid =
        !('error' in action.payload) &&
        action.payload.isLicensed &&
        action.payload.isAuthorized &&
        action.payload.isConfigured;
    });
    builder.addCase(CheckInEffects.checkIn.rejected, (mutableState, action) => {
      mutableState.isCustomerValid = false;
      mutableState.status = StateStatus.FAILED;
      mutableState.error = action.error;
    });
  },
});

export const { setCustomerValid } = CheckInState.actions;
export const getCheckInStatus = (state: RootState): StateStatus => state.checkInState.status;
export const isCustomerValid = (state: RootState): boolean => state.checkInState.isCustomerValid;
